import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { QuestionaireSummary } from './QuestionaireSummary';

interface QuestionData {
  id: string;
  question: string;
  type: 'yesno' | 'choice' | 'text';
  options?: string[];
  required?: boolean;
}

interface QuestionnaireData {
  name: string;
  age: string;
  state: string;
  coverageAmount: string;
  smokingStatus?: string;
  policyType?: string;
  healthStatus?: string;
  majorHealthConditions?: string;
  medications?: string;
  familyHistory?: string;
  riskyHobbies?: string;
  highRiskOccupation?: string;
  alcoholConsumption?: string;
  exercise?: string;
  priority?: string;
  noMedicalExam?: string;
  approvalPreference?: string;
}

interface GuidedQuestionnaireProps {
  initialData: Partial<QuestionnaireData>;
  onQuestionnaireUpdate: (data: Partial<QuestionnaireData>) => void;
  onSeeQuote: () => void;
  submitting?: boolean;
}

const QUESTIONS: QuestionData[] = [
  {
    id: 'smokingStatus',
    question: 'Do you currently smoke or use tobacco products?',
    type: 'yesno',
    required: true
  },
  {
    id: 'policyType',
    question: 'What type of life insurance are you most interested in?',
    type: 'choice',
    options: ['Term Life Insurance', 'Whole Life Insurance', "I'm not sure"],
    required: true
  },
  {
    id: 'healthStatus',
    question: 'How would you describe your overall health?',
    type: 'choice',
    options: ['Excellent', 'Good', 'Fair', 'Poor'],
    required: true
  },
  {
    id: 'majorHealthConditions',
    question: 'Do you have any major health conditions? (heart disease, diabetes, cancer, etc.)',
    type: 'yesno'
  },
  {
    id: 'medications',
    question: 'Are you currently taking any prescription medications?',
    type: 'yesno'
  },
  {
    id: 'familyHistory',
    question: 'Do you have a family history of heart disease, cancer, or other serious conditions?',
    type: 'yesno'
  },
  {
    id: 'riskyHobbies',
    question: 'Do you participate in any high-risk activities? (skydiving, rock climbing, racing, etc.)',
    type: 'yesno'
  },
  {
    id: 'highRiskOccupation',
    question: 'Do you work in a high-risk occupation? (military, mining, commercial fishing, etc.)',
    type: 'yesno'
  },
  {
    id: 'alcoholConsumption',
    question: 'Do you consume alcohol regularly?',
    type: 'choice',
    options: ['Never', 'Occasionally (1-2 drinks per week)', 'Moderately (3-7 drinks per week)', 'Frequently (8+ drinks per week)']
  },
  {
    id: 'exercise',
    question: 'How often do you exercise?',
    type: 'choice',
    options: ['Daily', 'Several times per week', 'Weekly', 'Rarely', 'Never']
  },
  {
    id: 'priority',
    question: "What's most important to you in a life insurance policy?",
    type: 'choice',
    options: ['Lowest premium cost', 'Flexible coverage options', 'Quick approval process', 'Comprehensive benefits'],
    required: true
  },
  {
    id: 'noMedicalExam',
    question: 'Would you prefer a policy that requires no medical exam?',
    type: 'yesno',
    required: true
  },
  {
    id: 'approvalPreference',
    question: 'How quickly do you need coverage?',
    type: 'choice',
    options: ['Immediately (instant approval)', 'Within a week', 'Within a month', "I'm flexible on timing"],
    required: true
  }
];

export const GuidedQuestionnaire: React.FC<GuidedQuestionnaireProps> = ({ 
  initialData, 
  onQuestionnaireUpdate, 
  onSeeQuote, 
  submitting = false 
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [cameFromBack, setCameFromBack] = useState(false);

  const currentQuestion = QUESTIONS[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / QUESTIONS.length) * 100;

  // Focus management for accessibility
  useEffect(() => {
    const heading = document.querySelector('[data-question-heading="true"]');
    if (heading) {
      (heading as HTMLElement).focus();
    }
  }, [currentQuestionIndex]);

  const handleAnswer = (answer: string) => {
    if (isAdvancing || submitting) return;
    
    const newAnswers = { ...answers, [currentQuestion.id]: answer };
    setAnswers(newAnswers);
    
    // Update parent with current questionnaire data
    onQuestionnaireUpdate({ ...newAnswers });
    
    // Auto-advance for single-choice questions (yesno and choice types)
    const isAutoAdvanceStep = currentQuestion.type === 'yesno' || currentQuestion.type === 'choice';
    
    if (isAutoAdvanceStep && currentQuestionIndex < QUESTIONS.length - 1) {
      setIsAdvancing(true);
      setCameFromBack(false);
      
      // Add delay for visual feedback
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
        setIsAdvancing(false);
      }, 220);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < QUESTIONS.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Final step - trigger quote generation
      onSeeQuote();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setCameFromBack(true);
    }
  };

  const canProceed = () => {
    const currentAnswer = answers[currentQuestion.id];
    return currentAnswer !== undefined && currentAnswer !== '';
  };


  return (
    <div className="flex justify-center">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Question {currentQuestionIndex + 1} of {QUESTIONS.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
          <CardHeader className="text-center pb-6">
            <CardTitle 
              className="text-2xl font-bold text-foreground"
              data-question-heading="true"
              id="question-heading"
              tabIndex={-1}
            >
              {currentQuestion.question}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {currentQuestion.type === 'yesno' && (
              <div className="grid grid-cols-2 gap-4" role="radiogroup" aria-labelledby="question-heading">
                <Button
                  variant={answers[currentQuestion.id] === 'yes' ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => handleAnswer('yes')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAnswer('yes');
                    }
                  }}
                  className="h-16 text-lg"
                  role="radio"
                  aria-checked={answers[currentQuestion.id] === 'yes'}
                >
                  Yes
                </Button>
                <Button
                  variant={answers[currentQuestion.id] === 'no' ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => handleAnswer('no')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAnswer('no');
                    }
                  }}
                  className="h-16 text-lg"
                  role="radio"
                  aria-checked={answers[currentQuestion.id] === 'no'}
                >
                  No
                </Button>
              </div>
            )}

            {currentQuestion.type === 'choice' && currentQuestion.options && (
              <div className="space-y-3" role="radiogroup" aria-labelledby="question-heading">
                {currentQuestion.options.map((option, index) => (
                  <Button
                    key={index}
                    variant={answers[currentQuestion.id] === option ? 'default' : 'outline'}
                    size="lg"
                    onClick={() => handleAnswer(option)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAnswer(option);
                      }
                    }}
                    className="w-full h-auto py-4 px-6 text-left justify-start text-wrap whitespace-normal"
                    role="radio"
                    aria-checked={answers[currentQuestion.id] === option}
                  >
                    {option}
                  </Button>
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-8">
              <div>
                {currentQuestionIndex > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    className="flex items-center"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                )}
              </div>
              
              <div>
                {(cameFromBack || currentQuestion.type === 'text' || currentQuestionIndex === QUESTIONS.length - 1) && (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={!canProceed() || submitting}
                    variant="hero"
                    className="flex items-center"
                  >
                    {currentQuestionIndex < QUESTIONS.length - 1 ? (
                      <>
                        Continue
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    ) : submitting ? (
                      'Getting Your Quote...'
                    ) : (
                      'See My Quote'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};